/**
 * Подготовка ответа дашборда к отрисовке. Считать здесь нечего: суммы,
 * сравнение с прошлым периодом и топы приходят готовыми из
 * `GET /admin/dashboard`. Остались выбор периода и оформление чисел.
 */
import { addDaysMs, formatShortDay, isoDay, parseIsoDay, startOfDayMs } from '@dorixona/utils'
import type { Point } from './api'

const DAY = 864e5

export interface Range {
  from: number
  to: number
  days: number
  /** Период задан датами вручную, а не кнопкой «7 дней». */
  custom: boolean
}

/**
 * Период: либо выбранные даты, либо последние N дней.
 *
 * «7 дней» — это семь полных суток, считая сегодняшние, поэтому отсчёт идёт от
 * полуночи, а не от текущего часа: иначе первый столбец графика охватывал бы
 * неполный день и всегда выглядел провалом.
 */
export function dashRange(
  now: number,
  days: number,
  from: number | null,
  to: number | null,
): Range {
  if (from !== null && to !== null && to >= from) {
    const start = startOfDayMs(from)
    const end = addDaysMs(startOfDayMs(to), 1) - 1
    return {
      from: start,
      to: end,
      days: Math.max(1, Math.round((end - start) / DAY)),
      custom: true,
    }
  }
  return { from: startOfDayMs(addDaysMs(now, -(days - 1))), to: now, days, custom: false }
}

/** Границы периода для запроса: сервер ждёт их днями, а не метками времени. */
export function rangeQuery(range: Range): { from: string; to: string } {
  return { from: isoDay(range.from), to: isoDay(range.to) }
}

/** Рост к прошлому периоду, %. Не с чем сравнивать — 0. */
export function deltaPercent(current: number, previous: number): number {
  return previous ? Math.round(((current - previous) / previous) * 100) : 0
}

export interface Bar {
  /** Подпись дня в том виде, в каком её показывает ось. */
  label: string
  sum: number
  n: number
}

/**
 * Столбец графика из точки ответа. Подпись приходит днём `YYYY-MM-DD` —
 * показываем её по-русски, но если сервер пришлёт что-то другое, оставляем
 * как есть: подпись оси важнее выдуманного формата.
 */
export function toBars(points: Point[]): Bar[] {
  return points.map((p) => {
    const day = parseIsoDay(p.label)
    return { label: day === null ? p.label : formatShortDay(day), sum: p.value, n: p.count }
  })
}

/** Верх шкалы — круглое число не меньше максимума столбца. */
export function niceTop(value: number): number {
  const power = Math.pow(10, Math.floor(Math.log10(value)))
  // Ни одна из дробных ступеней не накрыла значение — берём следующий разряд.
  return ([1, 2, 2.5, 5].find((m) => m * power >= value) ?? 10) * power
}

/** «1,2 млн» / «420 тыс» / «840» — подпись оси и узкой колонки. */
export function formatShortSum(value: number): string {
  if (value >= 1e6) return `${(Math.round(value / 1e5) / 10).toLocaleString('ru-RU')} млн`
  if (value >= 1e3) return `${Math.round(value / 1e3)} тыс`
  return Math.round(value).toLocaleString('ru-RU')
}

export interface TopRow {
  name: string
  /** Вторая строка: количество, когда в значении деньги, и наоборот. */
  note: string
  value: number
}

/**
 * Строка «топа». В деньгах ведём значение, а счёт уводим в подпись — и
 * наоборот: так одна и та же панель читается и для выручки, и для штук.
 */
export function toRows(points: Point[], unit: 'money' | 'count'): TopRow[] {
  return points.map((p) => ({
    name: p.label,
    note: unit === 'money' ? countNote(p.count) : '',
    value: unit === 'money' ? p.value : p.count,
  }))
}

function countNote(count: number): string {
  return count ? `${count} шт` : ''
}
